/**
 * ContractWizard Component
 * Main orchestrator for the contract create/edit workflow with stepper navigation
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Button,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Stop as TerminateIcon,
  Autorenew as RolloverIcon,
  EventRepeat as ExtendIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  AccountBalanceWallet as RedemptionIcon,
} from '@mui/icons-material';
import ExtendContractDialog from '../ExtendContractDialog';
import AdhocCapitalAmendmentDialog from '../AdhocCapitalAmendmentDialog';
import ContractActionDialogs from './steps/summary/ContractActionDialogs';
import SubmitForApprovalDialog from '../SubmitForApprovalDialog';
import { usePermissions } from '../../../contexts/PermissionContext';
import { MODULES, PERMISSION_LEVELS } from '../../../contexts/PermissionConstants';
import useServerPDF from '../../../hooks/useServerPDF';
import { useForm, FormProvider } from 'react-hook-form';
import { format } from 'date-fns';

import { ContractFormProvider, useContractFormContext } from './ContractFormContext';
import { useNotification } from '../../../contexts/NotificationContext';
import WizardStepper, { getSteps } from './WizardStepper';
import WizardNavigation from './WizardNavigation';
import ContractDetailsStep from './steps/ContractDetailsStep';
import InterestPaymentsStep from './steps/InterestPaymentsStep';
import CapitalEventsFeesStep from './steps/CapitalEventsFeesStep';
import FeesSecurityStep from './steps/FeesSecurityStep';
import ReviewScheduleStep from './steps/ReviewScheduleStep';
import ContractSummaryStep from './steps/ContractSummaryStep';
import useContractReferenceData from '../../../hooks/useContractReferenceData';
import useContractValidation from '../../../hooks/useContractValidation';
import { apiPost, apiPut, apiGet, apiDelete } from '../../../utils/apiClient';

// Default form values for new contracts
const getDefaultValues = () => ({
  is_multi_investor: false,
  investor_id: '',
  product_id: '',
  capital_amount: '',
  new_funds_amount: '',
  rolled_funds_amount: 0,
  user_reference: '',
  investment_currency_id: '',
  investment_term: 'Years',
  investment_term_value: '',
  deposit_date: format(new Date(), 'yyyy-MM-dd'),
  maturity_date: '',
  funds_received_date: '',
  first_payment_date: '',
  rate_type: 'Fixed',
  day_count_basis: '',
  likelihood_to_roll: '',
  interest_rate: '',
  interest_paid_frequency_id: '',
  capital_paid_frequency_id: '',
  benchmark_rate_id: null,
  rate_spread: 0,
  rate_change_effective_basis: 'immediate',
  rate_change_mode: 'bulk',
  exclude_leap_day: false,
  eligible_for_repricing: false,
  repricing_frequency_id: '',
  first_repricing_date: '',
  is_brokered: false,
  broker_id: '',
  broker_fees: '',
  issuer_id: '',
  program_id: '',
  stamp_duty: false,
  stamp_duty_fees: '',
  has_collateral: false,
  collateral_provider_id: '',
  collateral_type_id: '',
  collateral_amount: '',
  collateral_multiplier: '',
  collateral_in_favour_of: '',
  collateral_description: '',
  calculation_profile_id: '',
  capitalization_frequency_id: '',
  equal_installment: false,
  combine_payment_rows: false,
});

// Map contract data to form values for edit mode
const mapContractToFormValues = (contract) => {
  if (!contract) return getDefaultValues();

  return {
    is_multi_investor: contract.is_multi_investor || false,
    investor_id: contract.investor_id || '',
    product_id: contract.product_id || '',
    capital_amount: contract.capital_amount || '',
    new_funds_amount: contract.new_funds_amount || '',
    rolled_funds_amount: contract.rolled_funds_amount || 0,
    user_reference: contract.user_reference || '',
    rolled_from_contract_id: contract.rolled_from_contract_id || null,
    rolled_from_reference: contract.rolled_from_reference || '',
    rolled_from_user_reference: contract.rolled_from_user_reference || '',
    investment_currency_id: contract.investment_currency_id || contract.currency_id || '',
    investment_term: contract.investment_term || 'Years',
    investment_term_value: contract.investment_term_value || '',
    deposit_date: contract.deposit_date ? format(new Date(contract.deposit_date), 'yyyy-MM-dd') : '',
    maturity_date: contract.maturity_date ? format(new Date(contract.maturity_date), 'yyyy-MM-dd') : '',
    funds_received_date: contract.funds_received_date ? format(new Date(contract.funds_received_date), 'yyyy-MM-dd') : '',
    first_payment_date: contract.first_payment_date ? format(new Date(contract.first_payment_date), 'yyyy-MM-dd') : '',
    rate_type: contract.rate_type || 'Fixed',
    day_count_basis: contract.day_count_basis || '365',
    likelihood_to_roll: contract.likelihood_to_roll || '',
    interest_rate: contract.interest_rate || '',
    interest_paid_frequency_id: contract.interest_paid_frequency_id || '',
    capital_paid_frequency_id: contract.capital_paid_frequency_id || '',
    benchmark_rate_id: contract.benchmark_rate_id || null,
    rate_spread: contract.rate_spread || 0,
    rate_change_effective_basis: contract.rate_change_effective_basis || 'immediate',
    rate_change_mode: contract.rate_change_mode || 'bulk',
    exclude_leap_day: contract.exclude_leap_day || false,
    eligible_for_repricing: contract.eligible_for_repricing || false,
    repricing_frequency_id: contract.repricing_frequency_id || '',
    first_repricing_date: contract.first_repricing_date ? format(new Date(contract.first_repricing_date), 'yyyy-MM-dd') : '',
    is_brokered: contract.is_brokered || false,
    broker_id: contract.broker_id || '',
    broker_fees: contract.broker_fees || '',
    issuer_id: contract.issuer_id || '',
    program_id: contract.program_id || '',
    stamp_duty: contract.stamp_duty || false,
    stamp_duty_fees: contract.stamp_duty_fees || '',
    has_collateral: contract.has_collateral || false,
    collateral_provider_id: contract.collateral_provider_id || '',
    collateral_type_id: contract.collateral_type_id || '',
    collateral_amount: contract.collateral_amount || '',
    collateral_multiplier: contract.collateral_multiplier || '',
    collateral_in_favour_of: contract.collateral_in_favour_of || '',
    collateral_description: contract.collateral_description || '',
    calculation_profile_id: contract.calculation_profile_id || '',
    capitalization_frequency_id: contract.capitalization_frequency_id || '',
    equal_installment: contract.equal_installment ?? false,
    combine_payment_rows: contract.combine_payment_rows ?? false,
  };
};

// Inner component that has access to context
function ContractWizardInner({ initialStepOverride = null, scheduleJustGenerated = false }) {
  const navigate = useNavigate();
  const notify = useNotification();
  const { hasPermission, canApprove } = usePermissions();
  const canAdmin = hasPermission(MODULES.CONTRACT_MANAGEMENT, PERMISSION_LEVELS.ADMIN);
  const canActivate = canApprove(MODULES.CONTRACT_MANAGEMENT);
  const { downloadPDF, isGenerating: isGeneratingPDF } = useServerPDF();
  const [pendingRateChange, setPendingRateChange] = useState(null);
  const [extendDialogOpen, setExtendDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [terminateDialogOpen, setTerminateDialogOpen] = useState(false);
  const [rolloverDialogOpen, setRolloverDialogOpen] = useState(false);
  const [redemptionDialogOpen, setRedemptionDialogOpen] = useState(false);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [submitDialogContractId, setSubmitDialogContractId] = useState(null);
  const [createdContractId, setCreatedContractId] = useState(null);
  const autoSaveInFlight = React.useRef(false);
  const saveInFlight = React.useRef(false);

  const {
    isEdit,
    contractId,
    initialData,
    refDataLoading,
    refDataError,
    contractInvestors,
    setContractInvestors,
    originalContractInvestors,
    setOriginalContractInvestors,
    contractCollaterals,
    setContractCollaterals,
    originalContractCollaterals,
    setOriginalContractCollaterals,
    adhocCapitalEvents,
    setAdhocCapitalEvents,
    originalAdhocCapitalEvents,
    setOriginalAdhocCapitalEvents,
    adhocInterestPayments,
    setAdhocInterestPayments,
    originalAdhocInterestPayments,
    setOriginalAdhocInterestPayments,
    hasSchedule,
    setHasSchedule,
    setSchedulePreview,
    setGeneratingSchedule,
    setSubmitting,
    clearValidation,
    setValidationErrors,
    setValidationWarnings,
    completedSteps,
    markStepCompleted,
    showSummaryStep,
  } = useContractFormContext();

  // Determine initial step
  // Priority: 1) initialStepOverride (from navigation state), 2) Draft → step 0, 3) showSummaryStep → step 5, 4) default to 0
  const getInitialStep = () => {
    if (initialStepOverride !== null && initialStepOverride !== undefined) {
      return initialStepOverride;
    }
    // Draft contracts open at step 0 (editable wizard), non-Draft open at summary
    if (isEdit && initialData?.status === 'Draft') return 0;
    if (showSummaryStep) return 5; // Contract Summary step
    return 0; // Start at Contract Details
  };

  const [activeStep, setActiveStep] = useState(getInitialStep);

  // After create→edit redirect with scheduleJustGenerated flag, fire the success toast once on mount.
  useEffect(() => {
    if (scheduleJustGenerated) {
      notify.success('Amortization schedule has been generated. You can now submit for approval.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot on mount
  }, []);

  // Get the appropriate steps array
  const steps = getSteps(showSummaryStep);

  // Load reference data
  useContractReferenceData();

  // Form setup with react-hook-form
  const methods = useForm({
    defaultValues: isEdit ? mapContractToFormValues(initialData) : getDefaultValues(),
    mode: 'onChange',
  });

  const { handleSubmit, watch, reset } = methods;

  // Validation hook
  const { runStepValidation, runFullValidation, validateStep } = useContractValidation();

  // Get the effective contract ID — from props (edit mode) or from a previous auto-save (create mode)
  const getEffectiveContractId = () => contractId || createdContractId;

  // Load adhoc events and investors for edit mode
  useEffect(() => {
    if (isEdit && contractId) {
      loadContractInvestors();
      loadContractCollaterals();
      loadAdhocCapitalEvents();
      loadAdhocInterestPayments();
      checkScheduleExists();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load functions are defined below and run once when edit mode opens
  }, [isEdit, contractId]);

  // Load pending rate change if contract has amendment status
  useEffect(() => {
    if (isEdit && contractId && initialData?.amendment_status === 'Pending Rate Change') {
      apiGet(`/rate-changes/contract/${contractId}`)
        .then(res => setPendingRateChange(res?.data?.pending || null))
        .catch(err => console.error('Error loading pending rate change:', err));
    }
  }, [isEdit, contractId, initialData?.amendment_status]);

  // One-time initial form hydration in edit mode. We deliberately do NOT
  // re-reset on every initialData change: react-hook-form's reset() is
  // destructive — it discards any in-progress user edits the form holds.
  // This effect previously fired on every initialData change, which caused
  // a class of bugs where a user's edit (e.g. correcting funds_received_date)
  // got clobbered by the stale stored value when initialData was refreshed
  // (or simply by the second effect-run under React Strict Mode). The
  // initial-load case is still handled here, and any subsequent fresh-data
  // event (e.g. after a save round-trip) should patch specific fields
  // explicitly rather than blowing away the whole form.
  const initialResetDone = useRef(false);
  useEffect(() => {
    if (initialResetDone.current) return;
    if (isEdit && initialData) {
      reset(mapContractToFormValues(initialData));
      initialResetDone.current = true;
    }
  }, [isEdit, initialData, reset]);

  // Format ISO date strings (from API) to yyyy-MM-dd for HTML date inputs
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    try { return format(new Date(dateStr), 'yyyy-MM-dd'); }
    catch { return dateStr; }
  };

  const loadContractInvestors = async () => {
    try {
      // Refresh WHT snapshot from investor master before fetching, so master
      // updates in Stakeholder Management show up. Server enforces the same
      // status gate; this check just avoids a needless write on active contracts.
      if (initialData?.status === 'Draft' || initialData?.status === 'Pending Approval') {
        try {
          await apiPost(`/contracts/${contractId}/investors/refresh-wht`);
        } catch (refreshErr) {
          console.warn('WHT refresh failed:', refreshErr);
        }
      }
      const response = await apiGet(`/contracts/${contractId}/investors`);
      const investorsArray = response?.data || response || [];
      setContractInvestors(investorsArray);
      setOriginalContractInvestors(investorsArray);
    } catch (err) {
      console.error('Error loading contract investors:', err);
    }
  };

  const loadContractCollaterals = async () => {
    try {
      const response = await apiGet(`/contracts/${contractId}/collaterals`);
      const rows = response?.data || response || [];
      setContractCollaterals(rows);
      setOriginalContractCollaterals(rows);
    } catch (err) {
      console.error('Error loading contract collaterals:', err);
    }
  };

  const loadAdhocCapitalEvents = async () => {
    try {
      const response = await apiGet(`/contracts/${contractId}/adhoc-capital`);
      const events = response?.data || response || [];
      const mapped = events.map(e => ({ ...e, event_date: formatDateForInput(e.event_date), isNew: false }));
      setAdhocCapitalEvents(mapped);
      setOriginalAdhocCapitalEvents(mapped);
    } catch (err) {
      console.error('Error loading adhoc capital events:', err);
    }
  };

  const loadAdhocInterestPayments = async () => {
    try {
      const response = await apiGet(`/contracts/${contractId}/adhoc-interest-payments`);
      const events = response?.data || response || [];
      const mapped = events.map(e => ({ ...e, event_date: formatDateForInput(e.event_date), isNew: false }));
      setAdhocInterestPayments(mapped);
      setOriginalAdhocInterestPayments(mapped);
    } catch (err) {
      console.error('Error loading adhoc interest payments:', err);
    }
  };

  const checkScheduleExists = async () => {
    try {
      const response = await apiGet(`/amortization/contract/${contractId}`);
      const scheduleData = response?.data || response;
      setHasSchedule(scheduleData?.schedule?.length > 0);
    } catch (err) {
      console.warn('Error checking schedule existence, defaulting to regenerate mode:', err?.message || err);
      setHasSchedule(true);
    }
  };

  // Render a list of validation errors as a single toast message
  const fireValidationToast = (errors) => {
    if (!errors || errors.length === 0) return;
    notify.error(
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {errors.map((err, i) => <li key={i}>{err}</li>)}
      </Box>
    );
  };

  // Navigation handlers
  const handleNext = () => {
    const formValues = watch();
    const result = runStepValidation(activeStep, formValues);

    if (result.valid) {
      clearValidation();
      const stepToSave = activeStep;
      markStepCompleted(activeStep);
      setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));

      // Auto-save in the background (non-blocking)
      // Skip on step 4 (Review) — it has its own save flow via Generate Schedule
      if (stepToSave <= 3) {
        autoSaveDraft(stepToSave);
      }
    } else {
      fireValidationToast(result.errors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Live validity of the current step, recomputed each render against the
  // watched form state. validateStep is a pure function (no state writes),
  // so this is safe to call inline; runStepValidation cannot be used here
  // because it sets validation-error state and would cause an infinite render
  // loop. Used to drive the Next button's disabled state — the user gets
  // immediate visual feedback as they fill in required fields rather than
  // having to click Next to learn the step is incomplete.
  // watch() (no args) subscribes this component to every form-state change.
  const watchedFormValues = watch();
  const isCurrentStepValid = (() => {
    try {
      const result = validateStep(activeStep, watchedFormValues);
      return !!result?.valid;
    } catch {
      return false;
    }
  })();

  const handleBack = () => {
    clearValidation();
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  // Handle clicking on a step in the stepper
  const handleStepClick = (stepIndex) => {
    // Can only click on completed steps
    if (completedSteps.includes(stepIndex)) {
      clearValidation();
      setActiveStep(stepIndex);
    }
  };

  const handleCancel = () => {
    navigate('/contracts');
  };

  const handleDownloadPDF = async () => {
    if (!contractId) return;
    try {
      const filename = `Contract_${initialData?.reference || contractId}_${format(new Date(), 'yyyyMMdd')}.pdf`;
      await downloadPDF({ type: 'contract', id: contractId, filename });
    } catch (err) {
      console.error('Error downloading contract PDF:', err);
      notify.error('Failed to generate contract PDF: ' + (err.message || 'Please try again.'));
    }
  };

  const handleActivate = async () => {
    try {
      await apiPost(`/contracts/${contractId}/activate`);
      window.location.reload();
    } catch (err) {
      console.error('Error activating contract:', err);
      notify.error('Failed to activate contract: ' + (err.response?.data?.message || err.message));
    }
  };

  // Build payload from form data
  const buildPayload = (data, status = 'Draft') => ({
    ...data,
    capital_amount: data.capital_amount ? parseFloat(data.capital_amount) : null,
    interest_rate: data.interest_rate ? parseFloat(data.interest_rate) : null,
    investment_term_value: data.investment_term_value ? parseInt(data.investment_term_value) : null,
    day_count_basis: data.day_count_basis,
    funds_received_date: data.funds_received_date || null,
    first_payment_date: data.first_payment_date || null,
    broker_fees: data.broker_fees ? parseFloat(data.broker_fees) : null,
    stamp_duty_fees: data.stamp_duty_fees ? parseFloat(data.stamp_duty_fees) : null,
    collateral_amount: data.collateral_amount ? parseFloat(data.collateral_amount) : null,
    collateral_multiplier: data.collateral_multiplier ? parseFloat(data.collateral_multiplier) : null,
    benchmark_rate_id: data.rate_type === 'Variable' ? (data.benchmark_rate_id || null) : null,
    rate_spread: data.rate_type === 'Variable' ? parseFloat(data.rate_spread || 0) : 0,
    rate_change_effective_basis: data.rate_type === 'Variable' ? (data.rate_change_effective_basis || 'immediate') : 'immediate',
    rate_change_mode: data.rate_type === 'Variable' ? (data.rate_change_mode || 'bulk') : 'bulk',
    exclude_leap_day: data.exclude_leap_day || false,
    eligible_for_repricing: data.eligible_for_repricing === true,
    repricing_frequency_id: data.eligible_for_repricing === true && data.repricing_frequency_id
      ? parseInt(data.repricing_frequency_id, 10)
      : null,
    program_id: data.program_id ? parseInt(data.program_id, 10) : null,
    status,
    is_multi_investor: data.is_multi_investor,
    investor_id: data.is_multi_investor ? null : data.investor_id,
    likelihood_to_roll: data.is_multi_investor ? null : (data.likelihood_to_roll || null),
  });

  // Save adhoc capital events
  const saveAdhocCapitalEvents = async (targetContractId) => {
    // Delete events that were removed from the form (existed in DB but no longer in current array)
    const currentCapitalIds = new Set(adhocCapitalEvents.filter(e => e.id).map(e => e.id));
    const removedCapitalEvents = originalAdhocCapitalEvents.filter(e => e.id && !currentCapitalIds.has(e.id));
    for (const event of removedCapitalEvents) {
      await apiDelete(`/contracts/${targetContractId}/adhoc-capital/${event.id}`);
    }

    // Create or update remaining events
    for (const event of adhocCapitalEvents) {
      if (!event.event_date || !event.amount) continue;

      if (event.isNew || !event.id) {
        await apiPost(`/contracts/${targetContractId}/adhoc-capital`, {
          event_date: event.event_date,
          event_type: event.event_type,
          amount: parseFloat(event.amount),
          description: event.description || null,
        });
      } else {
        await apiPut(`/contracts/${targetContractId}/adhoc-capital/${event.id}`, {
          event_date: event.event_date,
          event_type: event.event_type,
          amount: parseFloat(event.amount),
          description: event.description || null,
        });
      }
    }
    // Reload from API to get real DB IDs and update originals snapshot
    try {
      const response = await apiGet(`/contracts/${targetContractId}/adhoc-capital`);
      const events = response?.data || response || [];
      const mapped = events.map(e => ({ ...e, event_date: formatDateForInput(e.event_date), isNew: false }));
      setAdhocCapitalEvents(mapped);
      setOriginalAdhocCapitalEvents(mapped);
    } catch (err) {
      console.error('Error reloading adhoc capital events:', err);
    }
  };

  // Save adhoc interest payment events
  const saveAdhocInterestPayments = async (targetContractId) => {
    // Delete events that were removed from the form
    const currentInterestIds = new Set(adhocInterestPayments.filter(e => e.id).map(e => e.id));
    const removedInterestEvents = originalAdhocInterestPayments.filter(e => e.id && !currentInterestIds.has(e.id));
    for (const event of removedInterestEvents) {
      await apiDelete(`/contracts/${targetContractId}/adhoc-interest-payments/${event.id}`);
    }

    // Create or update remaining events
    for (const event of adhocInterestPayments) {
      if (!event.event_date) continue;

      if (event.isNew || !event.id) {
        await apiPost(`/contracts/${targetContractId}/adhoc-interest-payments`, {
          event_date: event.event_date,
          description: event.description || null,
          amount: event.amount != null && event.amount !== '' ? parseFloat(event.amount) : null,
        });
      } else {
        await apiPut(`/contracts/${targetContractId}/adhoc-interest-payments/${event.id}`, {
          event_date: event.event_date,
          description: event.description || null,
          amount: event.amount != null && event.amount !== '' ? parseFloat(event.amount) : null,
        });
      }
    }
    // Reload from API to get real DB IDs and update originals snapshot
    try {
      const response = await apiGet(`/contracts/${targetContractId}/adhoc-interest-payments`);
      const events = response?.data || response || [];
      const mapped = events.map(e => ({ ...e, event_date: formatDateForInput(e.event_date), isNew: false }));
      setAdhocInterestPayments(mapped);
      setOriginalAdhocInterestPayments(mapped);
    } catch (err) {
      console.error('Error reloading adhoc interest payments:', err);
    }
  };

  // Save contract investors (edit mode — diff-based: delete removed, create new, update changed)
  const saveContractInvestors = async (targetContractId) => {
    const currentInvestorIds = new Set(contractInvestors.map(i => i.investor_id));
    const originalInvestorIds = new Set(originalContractInvestors.map(i => i.investor_id));

    // Delete removed investors
    const removedInvestors = originalContractInvestors.filter(i => !currentInvestorIds.has(i.investor_id));
    for (const investor of removedInvestors) {
      await apiDelete(`/contracts/${targetContractId}/investors/${investor.investor_id}`);
    }

    // Create new investors or update existing ones
    for (const investor of contractInvestors) {
      const investorPayload = {
        investor_id: investor.investor_id,
        capital_amount: parseFloat(investor.capital_amount),
        new_funds_amount: parseFloat(investor.new_funds_amount) || 0,
        rolled_funds_amount: parseFloat(investor.rolled_funds_amount) || 0,
        likelihood_to_roll: investor.likelihood_to_roll || null,
      };

      if (!originalInvestorIds.has(investor.investor_id)) {
        // New investor
        await apiPost(`/contracts/${targetContractId}/investors`, investorPayload);
      } else {
        // Existing investor — update
        await apiPut(`/contracts/${targetContractId}/investors/${investor.investor_id}`, investorPayload);
      }
    }

    // Reload from API to sync state
    try {
      const response = await apiGet(`/contracts/${targetContractId}/investors`);
      const investorsArray = response?.data || response || [];
      setContractInvestors(investorsArray);
      setOriginalContractInvestors(investorsArray);
    } catch (err) {
      console.error('Error reloading contract investors:', err);
    }
  };

  // Save contract collaterals (edit + create modes — diff-based against the
  // originally-loaded snapshot: delete removed rows, create new ones, update
  // changed ones). Rows that did not exist server-side have no `id`.
  const saveContractCollaterals = async (targetContractId) => {
    const currentIds = new Set(contractCollaterals.filter(c => c.id !== undefined).map(c => c.id));
    const originalIds = (originalContractCollaterals || []).map(c => c.id).filter(id => id !== undefined);

    // Delete rows that existed before and are gone now
    for (const oldId of originalIds) {
      if (!currentIds.has(oldId)) {
        await apiDelete(`/contracts/${targetContractId}/collaterals/${oldId}`);
      }
    }

    for (const row of contractCollaterals) {
      const payload = {
        collateral_provider_id: row.collateral_provider_id,
        secured: row.secured !== false,
        currency_id: row.currency_id ?? null,
        collateral_amount: row.collateral_amount,
        collateral_type_id: row.collateral_type_id ?? null,
        collateral_multiplier: row.collateral_multiplier ?? null,
        nominal_amount: row.nominal_amount ?? null,
        collateral_in_favour_of: row.collateral_in_favour_of ?? null,
        collateral_description: row.collateral_description ?? null,
        contingent_liability: row.contingent_liability ?? null,
      };
      if (row.id === undefined) {
        await apiPost(`/contracts/${targetContractId}/collaterals`, payload);
      } else {
        await apiPut(`/contracts/${targetContractId}/collaterals/${row.id}`, payload);
      }
    }

    // Reload to capture server-generated ids for new rows
    try {
      const response = await apiGet(`/contracts/${targetContractId}/collaterals`);
      const rows = response?.data || response || [];
      setContractCollaterals(rows);
      setOriginalContractCollaterals(rows);
    } catch (err) {
      console.error('Error reloading contract collaterals:', err);
    }
  };

  // Auto-save draft in the background (called during step transitions).
  // Does NOT navigate away. Does NOT validate (validation already done in handleNext).
  const autoSaveDraft = async (fromStep) => {
    if (autoSaveInFlight.current || saveInFlight.current) return;
    autoSaveInFlight.current = true;
    saveInFlight.current = true;
    try {
      const formData = watch();
      const payload = buildPayload(formData, 'Draft');
      const effectiveId = getEffectiveContractId();

      if (effectiveId) {
        await apiPut(`/contracts/${effectiveId}`, payload);
      } else {
        // Create mode, first save
        const result = await apiPost('/contracts', payload);
        const newId = result?.data?.id || result?.id;
        if (newId) {
          setCreatedContractId(newId);
          if (formData.is_multi_investor && contractInvestors.length > 0) {
            await saveContractInvestors(newId);
          }
          if (formData.has_collateral && contractCollaterals.length > 0) {
            await saveContractCollaterals(newId);
          }
          return;
        } else {
          notify.warning('Contract was saved but the reference could not be captured. Use "Save & Close" to save explicitly.');
          return;
        }
      }

      // Save related entities based on which step we're leaving
      const targetId = effectiveId || createdContractId;
      if (!targetId) return;

      if (fromStep === 0 && formData.is_multi_investor) {
        await saveContractInvestors(targetId);
      }
      // Collateral is captured on the Fees & Security step; sync it whenever
      // the user advances past that step (or any later one) so partial work
      // is not lost between auto-saves.
      if (formData.has_collateral && fromStep >= 3) {
        await saveContractCollaterals(targetId);
      }
      if (fromStep === 2) {
        await saveAdhocCapitalEvents(targetId);
        await saveAdhocInterestPayments(targetId);
      }
    } catch (err) {
      console.warn('Auto-save failed:', err);
      notify.warning('Auto-save failed: ' + (err.response?.data?.message || err.message || 'Unknown error') + '. Your data is still in the form.');
    } finally {
      autoSaveInFlight.current = false;
      saveInFlight.current = false;
    }
  };

  // Save & close
  const handleSaveDraft = async (data) => {
    if (saveInFlight.current) return;
    saveInFlight.current = true;
    try {
      setSubmitting(true);
      clearValidation();

      // Validate current step at minimum
      const stepResult = runStepValidation(activeStep, data);
      if (!stepResult.valid) {
        setSubmitting(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      const payload = buildPayload(data, 'Draft');
      const effectiveId = getEffectiveContractId();

      if (effectiveId) {
        // Update existing contract (edit mode OR create mode after auto-save created it)
        await apiPut(`/contracts/${effectiveId}`, payload);
        if (data.is_multi_investor) {
          await saveContractInvestors(effectiveId);
        }
        if (data.has_collateral) {
          await saveContractCollaterals(effectiveId);
        } else if ((originalContractCollaterals || []).length > 0) {
          // User toggled has_collateral off — wipe any server-side rows.
          await saveContractCollaterals(effectiveId);
        }
        await saveAdhocCapitalEvents(effectiveId);
        await saveAdhocInterestPayments(effectiveId);
        // Save & Close always exits the wizard back to the contracts list,
        // regardless of edit vs create-after-autosave. Previously edit mode
        // routed to the contract summary which surprised users — the button
        // is "Save & Close", not "Save & View".
        navigate('/contracts');
      } else {
        // Create new contract (no prior auto-save)
        const result = await apiPost('/contracts', payload);
        const newContractId = result?.data?.id || result?.id;

        // Add investors for multi-investor contracts
        if (data.is_multi_investor && newContractId && contractInvestors.length > 0) {
          for (const investor of contractInvestors) {
            await apiPost(`/contracts/${newContractId}/investors`, {
              investor_id: investor.investor_id,
              capital_amount: parseFloat(investor.capital_amount),
              new_funds_amount: parseFloat(investor.new_funds_amount) || 0,
              rolled_funds_amount: parseFloat(investor.rolled_funds_amount) || 0,
              likelihood_to_roll: investor.likelihood_to_roll || null,
            });
          }
        }

        // Add collaterals
        if (data.has_collateral && newContractId && contractCollaterals.length > 0) {
          await saveContractCollaterals(newContractId);
        }

        // Add adhoc events
        await saveAdhocCapitalEvents(newContractId);
        await saveAdhocInterestPayments(newContractId);

        navigate('/contracts');
      }
    } catch (err) {
      console.error('Error saving contract:', err);
      notify.error(err.response?.data?.message || 'Failed to save contract');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
      saveInFlight.current = false;
    }
  };

  // Submit for approval — persists pending changes then opens the document
  // upload dialog. The dialog itself fires POST /contracts/:id/submit-for-approval
  // with the chosen document_id (hard-block: cannot submit without a document).
  const handleSubmitForApproval = async (data) => {
    if (saveInFlight.current) return;
    saveInFlight.current = true;
    try {
      setSubmitting(true);
      clearValidation();

      // Full validation before submission
      const validationResult = runFullValidation(data);
      if (!validationResult.valid) {
        fireValidationToast(validationResult.errors);
        setSubmitting(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      // Build payload with Draft status — the submit-for-approval call flips to
      // 'Pending Approval' once a document is attached via the dialog.
      const payload = buildPayload(data, 'Draft');
      let targetId = getEffectiveContractId();

      if (targetId) {
        await apiPut(`/contracts/${targetId}`, payload);
        if (data.is_multi_investor) {
          await saveContractInvestors(targetId);
        }
        if (data.has_collateral || (originalContractCollaterals || []).length > 0) {
          await saveContractCollaterals(targetId);
        }
        await saveAdhocCapitalEvents(targetId);
        await saveAdhocInterestPayments(targetId);
      } else {
        // Create then continue with the new id (shouldn't happen - submit disabled in create mode)
        const result = await apiPost('/contracts', payload);
        targetId = result?.data?.id || result?.id;
        if (!targetId) {
          throw new Error('Failed to create contract — no id returned');
        }
      }

      setSubmitDialogContractId(targetId);
      setSubmitDialogOpen(true);
    } catch (err) {
      console.error('Error preparing contract for submission:', err);
      notify.error(err.response?.data?.message || 'Failed to save contract before submission');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
      saveInFlight.current = false;
    }
  };

  const handleSubmissionDialogClose = () => {
    setSubmitDialogOpen(false);
    setSubmitDialogContractId(null);
  };

  const handleSubmissionComplete = () => {
    setSubmitDialogOpen(false);
    const id = submitDialogContractId;
    setSubmitDialogContractId(null);
    navigate(isEdit && id ? `/contracts/${id}` : '/contracts');
  };

  // Generate/regenerate amortization schedule
  const handleGenerateSchedule = async () => {
    if (saveInFlight.current) return;
    saveInFlight.current = true;
    try {
      setGeneratingSchedule(true);

      const formData = watch();

      // Pre-flight: validate every prior step before generating a schedule.
      // Otherwise broken inputs (e.g. interest_rate = 0) silently produce a real
      // schedule and the error surfaces only at submit time.
      const allErrors = [];
      const allWarnings = [];
      for (let s = 0; s <= 3; s++) {
        const r = validateStep(s, formData);
        allErrors.push(...r.errors);
        allWarnings.push(...r.warnings);
      }
      if (allErrors.length > 0) {
        setValidationErrors(allErrors);
        setValidationWarnings(allWarnings);
        fireValidationToast(allErrors);
        setGeneratingSchedule(false);
        saveInFlight.current = false;
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      const payload = buildPayload(formData, 'Draft');

      let targetContractId = getEffectiveContractId();

      if (targetContractId) {
        // Edit mode or create mode after auto-save: update existing contract
        const contractStatus = initialData?.status;
        const isEditable = !contractStatus || contractStatus === 'Draft';
        if (isEditable) {
          await apiPut(`/contracts/${targetContractId}`, payload);
        }
        if (formData.is_multi_investor) {
          await saveContractInvestors(targetContractId);
        }
        if (formData.has_collateral || (originalContractCollaterals || []).length > 0) {
          await saveContractCollaterals(targetContractId);
        }
        await saveAdhocCapitalEvents(targetContractId);
        await saveAdhocInterestPayments(targetContractId);
      } else {
        // Create mode: create the contract first
        const result = await apiPost('/contracts', payload);
        targetContractId = result?.data?.id || result?.id;

        if (!targetContractId) {
          throw new Error('Failed to create contract - no ID returned');
        }

        setCreatedContractId(targetContractId);

        // Add investors for multi-investor contracts
        if (formData.is_multi_investor && contractInvestors.length > 0) {
          for (const investor of contractInvestors) {
            await apiPost(`/contracts/${targetContractId}/investors`, {
              investor_id: investor.investor_id,
              capital_amount: parseFloat(investor.capital_amount),
              new_funds_amount: parseFloat(investor.new_funds_amount) || 0,
              rolled_funds_amount: parseFloat(investor.rolled_funds_amount) || 0,
              likelihood_to_roll: investor.likelihood_to_roll || null,
            });
          }
        }

        // Add collaterals
        if (formData.has_collateral && contractCollaterals.length > 0) {
          await saveContractCollaterals(targetContractId);
        }

        // Add adhoc events
        await saveAdhocCapitalEvents(targetContractId);
        await saveAdhocInterestPayments(targetContractId);
      }

      // Generate the schedule
      const response = hasSchedule
        ? await apiPost(`/amortization/regenerate/${targetContractId}`)
        : await apiPost(`/amortization/generate/${targetContractId}`);

      const scheduleData = response?.data || response;
      if (scheduleData?.schedule?.length > 0) {
        setSchedulePreview(scheduleData);
        setHasSchedule(true);
      } else {
        // Schedule generated but no data returned - fetch it
        const fetchResponse = await apiGet(`/amortization/contract/${targetContractId}`);
        const fetchedData = fetchResponse?.data || fetchResponse;
        if (fetchedData?.schedule?.length > 0) {
          setSchedulePreview(fetchedData);
        }
        setHasSchedule(true);
      }

      // If this was a create, redirect to edit mode for the new contract
      // Use state to indicate we should stay on the Review & Schedule step (index 4)
      // and to show the success toast after the remount.
      if (!isEdit && targetContractId) {
        navigate(`/contracts/${targetContractId}/edit`, { state: { initialStep: 4, scheduleJustGenerated: true } });
      } else {
        notify.success('Amortization schedule has been generated. You can now submit for approval.');
      }
    } catch (err) {
      console.error('Error generating schedule:', err);
      notify.error(err.response?.data?.message || 'Failed to generate amortization schedule');
    } finally {
      setGeneratingSchedule(false);
      saveInFlight.current = false;
    }
  };

  // Handle navigating to edit mode from summary step
  const handleEditContract = () => {
    clearValidation();
    setActiveStep(0); // Go to first step to edit
  };

  // Render step content
  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return <ContractDetailsStep />;
      case 1:
        return <InterestPaymentsStep />;
      case 2:
        return <CapitalEventsFeesStep />;
      case 3:
        return <FeesSecurityStep />;
      case 4:
        return (
          <ReviewScheduleStep
            onGenerateSchedule={handleGenerateSchedule}
          />
        );
      case 5:
        return (
          <ContractSummaryStep
            onEditContract={handleEditContract}
          />
        );
      default:
        return null;
    }
  };

  // Loading state
  if (refDataLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (refDataError) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {refDataError}
        </Alert>
        <Button variant="outlined" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <FormProvider {...methods}>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4">
              {isEdit ? (initialData?.user_reference || initialData?.reference || 'Contract Management') : 'Create New Contract'}
            </Typography>
            {isEdit && initialData && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                {initialData.user_reference && (
                  <Typography variant="body2" color="textSecondary">
                    {initialData.reference}
                  </Typography>
                )}
                <Chip label={initialData.status} size="small" color="default" />
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Pending Approval actions */}
            {isEdit && canAdmin && initialData?.status === 'Pending Approval' && (
              <>
                <Button variant="contained" color="success" startIcon={<ApproveIcon />} onClick={() => setApproveDialogOpen(true)}>
                  Approve
                </Button>
                <Button variant="outlined" color="error" startIcon={<RejectIcon />} onClick={() => setRejectDialogOpen(true)}>
                  Reject
                </Button>
              </>
            )}

            {/* Approved actions */}
            {isEdit && canActivate && initialData?.status === 'Approved' && (
              <Button variant="contained" color="primary" startIcon={<ApproveIcon />} onClick={handleActivate}>
                Activate Contract
              </Button>
            )}

            {/* Active actions */}
            {isEdit && canAdmin && initialData?.status === 'Active' && (
              <>
                {!initialData.amendment_status && (
                  <Button variant="contained" color="info" startIcon={<RedemptionIcon />} onClick={() => setRedemptionDialogOpen(true)}>
                    Capital Redemption
                  </Button>
                )}
                <Button variant="outlined" color="warning" startIcon={<RolloverIcon />} onClick={() => setRolloverDialogOpen(true)}>
                  Rollover
                </Button>
                <Button variant="outlined" color="error" startIcon={<TerminateIcon />} onClick={() => setTerminateDialogOpen(true)}>
                  Early Termination
                </Button>
              </>
            )}

            {/* Matured actions */}
            {isEdit && canAdmin && initialData?.status === 'Matured' && (
              <>
                {!initialData.amendment_status && (
                  <>
                    <Button variant="contained" color="primary" startIcon={<ExtendIcon />} onClick={() => setExtendDialogOpen(true)}>
                      Extend
                    </Button>
                    <Button variant="contained" color="info" startIcon={<RedemptionIcon />} onClick={() => setRedemptionDialogOpen(true)}>
                      Capital Redemption
                    </Button>
                  </>
                )}
                <Button variant="outlined" color="warning" startIcon={<RolloverIcon />} onClick={() => setRolloverDialogOpen(true)}>
                  Rollover
                </Button>
                <Button variant="outlined" color="error" startIcon={<TerminateIcon />} onClick={() => setTerminateDialogOpen(true)}>
                  Terminate
                </Button>
              </>
            )}

            {isEdit && (
              <Button variant="outlined" startIcon={<BackIcon />} onClick={() => navigate('/contracts')}>
                Back to Contract List
              </Button>
            )}
          </Box>
        </Box>

        {/* Pending Rate Change Banner */}
        {isEdit && initialData?.amendment_status === 'Pending Rate Change' && pendingRateChange && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => navigate('/approvals')}>
                View in Approvals
              </Button>
            }
          >
            <strong>Pending Rate Change:</strong> {pendingRateChange.old_rate}% → {pendingRateChange.new_rate}%
            {pendingRateChange.effective_date && ` (effective ${format(new Date(pendingRateChange.effective_date), 'dd MMM yyyy')})`}
          </Alert>
        )}

        {/* Matured Contract Action Required Banner */}
        {isEdit && initialData?.status === 'Matured' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Action Required:</strong>{' '}
            This contract matured on {initialData.maturity_date ? format(new Date(initialData.maturity_date), 'dd MMM yyyy') : 'N/A'}.
            Please <strong>Extend</strong>, <strong>Rollover</strong>, or <strong>Terminate</strong> this contract.
          </Alert>
        )}

        {/* Pending Extension Banner */}
        {isEdit && initialData?.amendment_status === 'Pending Extension' && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => navigate('/approvals')}>
                View in Approvals
              </Button>
            }
          >
            <strong>Pending Extension:</strong> This contract has a pending extension request awaiting approval.
          </Alert>
        )}

        {/* Stepper */}
        <WizardStepper
          activeStep={activeStep}
          completedSteps={completedSteps}
          onStepClick={handleStepClick}
          showSummaryStep={showSummaryStep}
        />

        {/* Navigation */}
        <WizardNavigation
          activeStep={activeStep}
          totalSteps={steps.length}
          showSummaryStep={showSummaryStep}
          nextDisabled={!isCurrentStepValid}
          onBack={handleBack}
          onNext={handleNext}
          onSaveDraft={handleSubmit(handleSaveDraft)}
          onSubmitForApproval={handleSubmit(handleSubmitForApproval)}
          onCancel={handleCancel}
          onEditContract={handleEditContract}
          onDownloadPDF={handleDownloadPDF}
          onGenerateSchedule={handleGenerateSchedule}
          isGeneratingPDF={isGeneratingPDF}
        />

        {/* Step Content */}
        {renderStep()}

        {/* Extend Contract Dialog */}
        {isEdit && initialData && (
          <ExtendContractDialog
            open={extendDialogOpen}
            onClose={() => setExtendDialogOpen(false)}
            contract={initialData}
            onSubmitSuccess={() => window.location.reload()}
          />
        )}

        {/* Capital Redemption Dialog */}
        {isEdit && initialData && (
          <AdhocCapitalAmendmentDialog
            open={redemptionDialogOpen}
            onClose={() => setRedemptionDialogOpen(false)}
            contract={initialData}
            onSubmitSuccess={() => window.location.reload()}
          />
        )}

        {/* Submit for Approval Dialog (mandatory signed-contract document) */}
        <SubmitForApprovalDialog
          open={submitDialogOpen}
          contractId={submitDialogContractId}
          onClose={handleSubmissionDialogClose}
          onSubmitted={handleSubmissionComplete}
        />

        {/* Action Dialogs (Approve, Reject, Terminate, Rollover) */}
        {isEdit && initialData && (
          <ContractActionDialogs
            contractId={contractId}
            contract={initialData}
            approveDialogOpen={approveDialogOpen}
            setApproveDialogOpen={setApproveDialogOpen}
            rejectDialogOpen={rejectDialogOpen}
            setRejectDialogOpen={setRejectDialogOpen}
            terminateDialogOpen={terminateDialogOpen}
            setTerminateDialogOpen={setTerminateDialogOpen}
            rolloverDialogOpen={rolloverDialogOpen}
            setRolloverDialogOpen={setRolloverDialogOpen}
            onActionComplete={() => window.location.reload()}
          />
        )}
      </Box>
    </FormProvider>
  );
}

// Main component with provider wrapper
function ContractWizard({ mode = 'create', contractId = null, initialData = null, initialStep = null, scheduleJustGenerated = false }) {
  return (
    <ContractFormProvider mode={mode} contractId={contractId} initialData={initialData}>
      <ContractWizardInner initialStepOverride={initialStep} scheduleJustGenerated={scheduleJustGenerated} />
    </ContractFormProvider>
  );
}

export default ContractWizard;
